/**
 * Payoff Curve — P&L at expiry (solid green) and today (dashed blue)
 * Break-even lines (purple dotted), spot marker (amber), leg strikes (grey dotted)
 */
import React from 'react';
import {
  ComposedChart, Line, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer, Legend
} from 'recharts';
import { Box, Typography } from '@mui/material';
import { BasketData } from '../../store/optionsSlice';
import { useAppTheme } from '../../hooks/useAppTheme';

interface Props {
  basket: BasketData;
  spotPrice: number;
}

const PayoffCurve: React.FC<Props> = ({ basket, spotPrice }) => {
  const { C } = useAppTheme();
  if (!basket.payoffData || !basket.payoffData.spotRange) return null;
  const { spotRange, expiry, today } = basket.payoffData;

  const data = spotRange.map((s, i) => ({
    spot: s,
    'At Expiry': expiry[i],
    'Today': today[i]
  }));

  const maxProfit = Number(basket.maxProfit || 0);
  const maxLoss = Number(basket.maxLoss || 0);

  return (
    <Box>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>
        Payoff Curve — {basket.strategy}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.secondary', mb: 1, display: 'block' }}>
        Max Profit: ₹{maxProfit.toFixed(0)} | Max Loss: ₹{Math.abs(maxLoss).toFixed(0)} |
        Break-evens: {(basket.breakEvens || []).join(', ') || '—'}
      </Typography>
      <ResponsiveContainer width="100%" height={240}>
        <ComposedChart data={data} margin={{ top: 5, right: 15, left: 5, bottom: 5 }}>
          <defs>
            <linearGradient id="profitGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={C.chartBull} stopOpacity={0.2} />
              <stop offset="95%" stopColor={C.chartBull} stopOpacity={0} />
            </linearGradient>
            <linearGradient id="lossGrad" x1="0" y1="1" x2="0" y2="0">
              <stop offset="5%" stopColor={C.chartBear} stopOpacity={0.2} />
              <stop offset="95%" stopColor={C.chartBear} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} opacity={0.4} />
          <XAxis dataKey="spot" tickFormatter={v => `₹${(v / 1000).toFixed(1)}K`} tick={{ fontSize: 10 }} />
          <YAxis tickFormatter={v => `₹${v.toFixed(0)}`} tick={{ fontSize: 10 }} />
          <Tooltip
            formatter={(v: any, name: string) => [`₹${Number(v).toFixed(0)}`, name]}
            labelFormatter={l => `Spot: ₹${l}`}
            contentStyle={{ backgroundColor: C.chartTooltipBg, border: `1px solid ${C.chartTooltipBorder}` }}
          />
          <Legend />
          <ReferenceLine y={0} stroke={C.chartGrid} strokeWidth={1} />
          <ReferenceLine x={spotPrice} stroke={C.spot} strokeWidth={2} label={{ value: 'Spot', fill: C.spot, fontSize: 11 }} />
          {(basket.breakEvens || []).map(be => (
            <ReferenceLine key={be} x={be} stroke={C.accent} strokeDasharray="4 4" />
          ))}
          {(basket.legs || []).map(leg => (
            <ReferenceLine key={leg.strike} x={leg.strike} stroke={C.chartGrid} strokeDasharray="2 4" />
          ))}
          <Line type="monotone" dataKey="At Expiry" stroke={C.chartBull} strokeWidth={2.5} dot={false} />
          <Line type="monotone" dataKey="Today" stroke={C.chartLine1} strokeWidth={1.5} strokeDasharray="6 3" dot={false} />
        </ComposedChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default PayoffCurve;
